import { z, type ZodTypeAny } from 'zod';

import { PHONE_REGEX } from '@libs/constants';

import type { ActiveTemplateField, ProfileFieldValidation } from '../../api/types';

/**
 * Builds a Zod schema (and matching default values) for a category's edit form
 * from each field's `type` and `validation` rules. Empty input arrives as
 * `null` (scalars) or `[]` (lists/tables) and always passes — saving is partial
 * and never requires a complete category — so the schema only enforces
 * format/length/range constraints on values the user actually entered.
 */

export type FieldFormValues = Record<string, unknown>;

const LIST_TYPES = new Set(['multi_select', 'string_list', 'number_list', 'list']);

// Types whose empty default value is an array (`[]`). `file` holds an array of
// uploaded-file descriptors; `reference` is a composite object and defaults to
// `null` like a scalar.
const isListLike = (type: string) =>
  LIST_TYPES.has(type) || type === 'table' || type === 'file';

/** Zod schema for a `reference` composite value (`{ text, source }`). */
function referenceSchema(field: ActiveTemplateField): ZodTypeAny {
  const v: ProfileFieldValidation = field.validation ?? {};
  const source = z
    .object({ url: z.string().url().optional(), note: z.string().optional() })
    .nullable();
  const schema = z.object({ text: stringSchema(field), source: source.optional() });
  if (v.sourceRequired) {
    return schema.refine(
      (val) => !!(val.source && (val.source.url || val.source.note)),
      { message: 'source required', path: ['source'] },
    );
  }
  return schema;
}

function safeRegex(pattern: string): RegExp | null {
  try {
    return new RegExp(pattern);
  } catch {
    return null;
  }
}

function stringSchema(field: ActiveTemplateField): z.ZodString {
  const v: ProfileFieldValidation = field.validation ?? {};
  let s = field.type === 'email' ? z.string().email() : z.string();
  if (typeof v.minLength === 'number') s = s.min(v.minLength);
  if (typeof v.maxLength === 'number') s = s.max(v.maxLength);
  if (v.pattern) {
    const re = safeRegex(v.pattern);
    // Only a *usable* pattern replaces the type's own default check. An
    // unparseable one falls through, so a phone still gets PHONE_REGEX rather
    // than silently losing all format validation — matching reviewFormSchema.
    if (re) return s.regex(re);
  }
  // Default shape check for `phone`, mirroring the built-in one `email` gets
  // above. Only applied when the field declares no `pattern` of its own, so a
  // catalog-supplied rule still wins. Without it a phone field accepted any
  // free text.
  if (field.type === 'phone') s = s.regex(PHONE_REGEX, 'app.orgProfile.field.phoneError');
  return s;
}

function arrayOf(item: ZodTypeAny, v: ProfileFieldValidation): ZodTypeAny {
  let a = z.array(item);
  // `minItems` is not enforced: like `required`, it would force a table/list to
  // be filled before saving, but the form allows saving partial categories. We
  // keep `maxItems` since an upper bound never blocks an empty/partial save.
  if (typeof v.maxItems === 'number') a = a.max(v.maxItems);
  return a;
}

/** The "filled value" schema for a field, before nullability/required handling. */
function baseSchema(field: ActiveTemplateField): ZodTypeAny {
  const v: ProfileFieldValidation = field.validation ?? {};

  if (field.type === 'number') {
    let n = z.number();
    if (v.integerOnly) n = n.int();
    if (typeof v.min === 'number') n = n.min(v.min);
    if (typeof v.max === 'number') n = n.max(v.max);
    return n;
  }
  if (field.type === 'number_list') {
    let n = z.number();
    if (v.integerOnly) n = n.int();
    if (typeof v.min === 'number') n = n.min(v.min);
    if (typeof v.max === 'number') n = n.max(v.max);
    return arrayOf(n, v);
  }
  if (field.type === 'boolean') return z.boolean();
  if (field.type === 'table') return arrayOf(z.record(z.string(), z.unknown()), v);
  if (field.type === 'file') return z.array(z.unknown());
  if (field.type === 'reference') return referenceSchema(field);
  if (LIST_TYPES.has(field.type)) return arrayOf(stringSchema(field), v);

  // string / email / phone / date / select / textarea
  return stringSchema(field);
}

function fieldSchema(field: ActiveTemplateField): ZodTypeAny {
  // No `required` enforcement: empty values pass so the user can always save a
  // partial category. Only the field's format/length/range rules apply.
  return baseSchema(field).nullable();
}

export function buildCategorySchema(
  fields: ActiveTemplateField[],
): z.ZodType<FieldFormValues, FieldFormValues> {
  const shape: Record<string, ZodTypeAny> = {};
  for (const f of fields) shape[f.id] = fieldSchema(f);
  return z.object(shape) as unknown as z.ZodType<FieldFormValues, FieldFormValues>;
}

export function buildCategoryDefaults(
  fields: ActiveTemplateField[],
): FieldFormValues {
  const defaults: FieldFormValues = {};
  for (const f of fields) {
    defaults[f.id] = f.currentValue ?? (isListLike(f.type) ? [] : null);
  }
  return defaults;
}
